import React, { useEffect, useRef } from "react";
import { computeHeading } from "../utils/compute-heading.ts";
import { StreetViewConfigProvider, useStreetViewConfig } from "../utils/street-view-config-context.tsx";

// Tilt the camera slightly upward so upper-floor flats are in frame rather than
// the pavement. Matches the pitch the legacy property details page used.
const INITIAL_PITCH = 10;

interface PropertyStreetViewProps {
  lat: number;
  lon: number;
  isTabActive?: boolean;
  containerClass?: string;
}

export const PropertyStreetView: React.FC<PropertyStreetViewProps> = (props) => (
  <StreetViewConfigProvider>
    <PropertyStreetViewInner {...props} />
  </StreetViewConfigProvider>
);

const PropertyStreetViewInner: React.FC<PropertyStreetViewProps> = ({
  lat,
  lon,
  isTabActive,
  containerClass = "h-full w-full",
}) => {
  const streetViewConfig = useStreetViewConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);

  useEffect(() => {
    if (!streetViewConfig) return;
    let isMounted = true;

    const initStreetView = () => {
      const google = window.google;
      if (!containerRef.current || !isMounted) return;

      const position = { lat, lng: lon };
      const streetViewService = new google.maps.StreetViewService();

      const request: google.maps.StreetViewLocationRequest = {
        location: position,
        radius: 50,
        // Default is BEST, which weights "recognised points of interest" and image
        // quality - on a listed building that pulls in a tour of the landmark over
        // the road outside it. We want the closest camera to the property.
        preference: google.maps.StreetViewPreference.NEAREST,
        // Exclude indoor collections and PhotoSpheres: aiming a heading at the
        // property is meaningless from inside a shop. The legacy page did the same.
        sources: [google.maps.StreetViewSource.OUTDOOR],
      };

      void streetViewService.getPanorama(request, (data, status) => {
        if (!isMounted) return;

        if (status !== google.maps.StreetViewStatus.OK || !data?.location) {
          if (containerRef.current) {
            containerRef.current.innerHTML = "No Street View available";
          }
          return;
        }

        if (containerRef.current) {
          const location = data.location;
          const heading = getHeadingToProperty(location, lat, lon);

          panoramaRef.current = new google.maps.StreetViewPanorama(containerRef.current, {
            // Pin to the panorama the service resolved. Passing `position` here
            // would instead make the panorama run its own nearest-pano search,
            // which takes no sources/radius options - it could land on a different
            // panorama from the one `heading` was measured against.
            pano: location.pano,
            ...(heading === undefined ? {} : { pov: { heading, pitch: INITIAL_PITCH } }),
            zoom: 1,
            addressControl: false,
            linksControl: true,
            panControl: true,
            enableCloseButton: false,
            fullscreenControl: false,
            showRoadLabels: false,
            zoomControl: false,
            motionTracking: false,
            motionTrackingControl: false,
          });
        }
      });
    };

    loadGoogleMaps(streetViewConfig.googleMapsApiKey)
      .then(initStreetView)
      .catch(() => {
        if (containerRef.current) {
          containerRef.current.innerHTML = "Failed to load Google Maps";
        }
      });

    return () => {
      isMounted = false;
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [lat, lon]);

  useEffect(() => {
    const panorama = panoramaRef.current;
    if (!isTabActive || !panorama) return;

    // The tab's container is display:none while inactive, so a window resize in
    // the meantime leaves the panorama rendered at the old size and it comes back
    // blank. Triggering 'resize' revalidates it, as the legacy page did.
    //
    // Deliberately the only thing done on re-activation. Users move and pan to get
    // a better look at the property, then tab away to compare against the photos -
    // re-aiming here would throw that away every time they came back. Only the
    // initial view is ours to choose; after that the panorama is theirs.
    window.google.maps.event.trigger(panorama, "resize");
  }, [isTabActive]);

  return (
    <div className={containerClass}>
      <div ref={containerRef} className="h-full w-full flex bg-foggy-firth items-center justify-center" />
      <style>{`
        .gm-compass {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

/*
 * Works out which way the camera should face so the property is in view.
 *
 * getPanorama searches within a 50m radius, so the camera can sit some distance
 * from the property - the bearing has to be measured from where the camera
 * actually is. Returns undefined if the panorama location is unavailable, in
 * which case the caller should leave Street View's default heading alone.
 */
function getHeadingToProperty(location: google.maps.StreetViewLocation, lat: number, lon: number): number | undefined {
  const latLng = location.latLng;
  if (!latLng) return undefined;

  return computeHeading({ lat: latLng.lat(), lon: latLng.lng() }, { lat, lon });
}

/* -------------------------------
   Singleton Google Maps Loader
--------------------------------*/
let googleMapsPromise: Promise<void> | null = null;

function loadGoogleMaps(apiKey: string): Promise<void> {
  // Runtime check: google may not be loaded yet despite TS types saying otherwise
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (window.google?.maps?.StreetViewPanorama) return Promise.resolve();

  googleMapsPromise ??= new Promise((resolve, reject) => {
    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.body.appendChild(script);
  });

  return googleMapsPromise;
}
